import { displayName, parseIsoToDateOfBirth } from '../../../lib/dependents/helpers';
import { createEmptyPendingDependent } from '../OnboardingContext';

import type { IDependentFormValues } from './types';
import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IDependent } from '../../../hooks/queries/useDependents';
import type { DependentCount, IPendingDependent } from '../OnboardingContext';

/**
 * Convert a persisted dependent row into form-values shape. Used to seed
 * pending state on resume (count step) and to render edit forms (entry step)
 * — both paths need the same lossless server → form mapping.
 */
export const dependentToFormValues = (dep: {
  relationship: CaregiverRelationshipKind;
  isStep: boolean;
  isInLaw: boolean;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: string | null;
}): IDependentFormValues => ({
  relationship: dep.relationship,
  isStep: dep.isStep,
  isInLaw: dep.isInLaw,
  firstName: dep.firstName,
  lastName: dep.lastName,
  preferredName: dep.preferredName ?? '',
  dateOfBirth: parseIsoToDateOfBirth(dep.dateOfBirth),
});

/**
 * Build `count` pending slots, seeding the first `count` from existing server
 * dependents (carrying their `serverId` / `caregiverRelationshipId` so the
 * review step takes the update path, not a duplicate create) and padding any
 * remainder with empty slots. Used to hydrate `pendingDependents` on entry to
 * the loved-ones flow from any direction — forward, resume, or back-nav.
 */
export const seedPendingDependentsFromServer = (
  dependents: ReadonlyArray<IDependent>,
  count: DependentCount
): IPendingDependent[] =>
  Array.from({ length: count }, (_, i) => {
    const dep = dependents[i];
    if (!dep) {
      return createEmptyPendingDependent();
    }
    return {
      values: dependentToFormValues(dep),
      serverId: dep.id,
      caregiverRelationshipId: dep.caregiverRelationshipId,
    };
  });

/**
 * Grow or shrink an array to length `count`. Used when the count step changes
 * how many dependents are being collected — preserve filled-out forms when
 * possible, fill new slots with `fill()`.
 */
export const truncateForCount = <T>(arr: T[], count: number, fill: () => T): T[] => {
  if (arr.length === count) {
    return arr;
  }
  if (arr.length > count) {
    return arr.slice(0, count);
  }
  const next = [...arr];
  while (next.length < count) {
    next.push(fill());
  }
  return next;
};

/**
 * "Review details for X and Y" title — falls back when names are missing.
 * Accepts a structural shape so both pending-form values (`IDependentFormValues`)
 * and persisted server rows (`IDependent`) flow through one helper.
 */
export const buildReviewTitle = (
  dependents: ReadonlyArray<{ firstName: string; preferredName?: string | null }>
): string => {
  if (dependents.length === 0) {
    return 'Review your loved ones';
  }
  const names = dependents.map(displayName).filter((n) => n.length > 0);
  if (names.length === 0) {
    return 'Review your loved ones';
  }
  if (names.length === 1) {
    return `Review details for ${names[0]}`;
  }
  return `Review details for ${names[0]} and ${names[1]}`;
};
